/** /t/c/<courseId>/plan — syllabus in, breakdown out, and the review tree (STORY-046/048/049).
 * The teacher stays in control: AI nodes are drafts until approved, one by one or all at once. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import {
  addModule, addTopic, addTreeLo, approveAll, deleteLo, deleteModule, deleteTopic, generateBreakdown, getBreakdown,
  getAllocation, getCourse, getCoverage, getHostInfo, getItems, getSchedule, getSyllabus, listBreakdowns, manualBreakdown, mergeLo, noServerHere, pasteSyllabus, patchLo, patchModule, patchTopic,
  splitLo, uploadSyllabus, type Breakdown, type LoNode, type ModuleNode, type TopicNode,
} from "../teacher/api";
import type { SyllabusText } from "../api/types";
import { BreakdownTree, type NodeRef } from "../teacher/BreakdownTree";
import { AllocationPanel } from "../teacher/AllocationPanel";
import { SchedulePanel } from "../teacher/SchedulePanel";
import { TeacherChrome } from "../teacher/Chrome";
import { Button, DraftChip, useConfirm } from "../ui";

const BLOOMS = ["remember", "understand", "apply", "analyse", "evaluate", "create"];
const input = "field w-full";
const card = "flex flex-col gap-3 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm";

function countDrafts(b: Breakdown | undefined): number {
  if (!b) return 0;
  let n = 0;
  for (const m of b.modules) {
    if (m.review_status === "draft") n++;
    for (const tp of m.topics) {
      if (tp.review_status === "draft") n++;
      n += tp.los.filter((lo) => lo.review_status === "draft").length;
    }
  }
  return n;
}

function errText(e: unknown, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (e instanceof ApiError) {
    if (e.code === "lo_code_exists") return t("plan.codeExists");
    if (e.code === "prerequisite_cycle") return t("plan.cycle");
    // On this computer /ai lives on the server; say so instead of leaking the service's English.
    if (e.code === "ai_unavailable" || e.code === "offline" || e.code === "cloud_signin") return t("plan.needsAi");
    return t("plan.saveFailed", { error: e.detail });
  }
  return t("plan.saveFailed", { error: String(e) });
}

export function Plan() {
  const { courseId } = useParams({ strict: false }) as { courseId: string };
  const { t } = useTranslation();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const course = useQuery({ queryKey: ["course", courseId], queryFn: () => getCourse(courseId) });
  const versions = useQuery({ queryKey: ["breakdowns", courseId], queryFn: () => listBreakdowns(courseId) });
  const [versionId, setVersionId] = useState<string | null>(null);
  const currentId = versionId ?? versions.data?.[0]?.breakdown_id ?? null;
  const breakdown = useQuery({
    queryKey: ["breakdown", currentId],
    queryFn: () => getBreakdown(currentId as string),
    enabled: !!currentId,
    refetchInterval: (q) => (q.state.data && ["queued", "running"].includes(q.state.data.status) ? 1500 : false),
  });
  const [selected, setSelected] = useState<NodeRef | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["breakdown", currentId] });
    void qc.invalidateQueries({ queryKey: ["breakdowns", courseId] });
    void qc.invalidateQueries({ queryKey: ["course", courseId] });
  };
  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      refresh();
    } catch (e) {
      setError(errText(e, t));
    }
  };

  const b = breakdown.data;
  const drafts = countDrafts(b);
  const busy = b ? ["queued", "running"].includes(b.status) : false;

  // selected node, resolved against the latest tree
  const node = (() => {
    if (!b || !selected) return null;
    for (const m of b.modules) {
      if (selected.kind === "module" && m.module_id === selected.id) return { kind: "module" as const, module: m };
      for (const tp of m.topics) {
        if (selected.kind === "topic" && tp.topic_id === selected.id) return { kind: "topic" as const, topic: tp, module: m };
        for (const lo of tp.los) if (selected.kind === "lo" && lo.lo_id === selected.id) return { kind: "lo" as const, lo, topic: tp, module: m };
      }
    }
    return null;
  })();

  async function approveNode(ref: NodeRef) {
    await run(async () => {
      if (ref.kind === "module") await patchModule(ref.id, { review_status: "approved" });
      else if (ref.kind === "topic") await patchTopic(ref.id, { review_status: "approved" });
      else await patchLo(ref.id, { review_status: "approved" });
    });
  }

  async function onApproveAll() {
    if (!b) return;
    const ok = await confirm({ title: t("plan.approveAllTitle", { n: drafts }), body: t("plan.approveAllConfirm"), confirmLabel: t("plan.approveAll") });
    if (ok) await run(() => approveAll(b.breakdown_id));
  }

  const syllabus = useQuery({
    queryKey: ["syllabus", courseId],
    queryFn: () => getSyllabus(courseId),
    retry: false,
    // a scanned PDF waits for the OCR job (STORY-053)
    refetchInterval: (q) => (q.state.data?.ocr_status === "queued" || q.state.data?.ocr_status === "running" ? 3000 : false),
  });
  const schedule = useQuery({ queryKey: ["schedule", courseId], queryFn: () => getSchedule(courseId), retry: false });
  const items = useQuery({ queryKey: ["items", courseId], queryFn: () => getItems(courseId) });
  const allocation = useQuery({ queryKey: ["allocation", courseId], queryFn: () => getAllocation(courseId) });
  const coverage = useQuery({ queryKey: ["coverage", courseId], queryFn: () => getCoverage(courseId) });
  const [step, setStep] = useState<StepKey | null>(null);
  const hasTree = !!b && (b.status === "done" || b.status === "manual") && b.modules.length > 0;
  const steps: StepInfo[] = [
    { key: "syllabus", done: !!syllabus.data, status: syllabus.data ? t("plan.saved", { chars: syllabus.data.chars, source: syllabus.data.source }) : t("plan.journey.syllabusTodo") },
    { key: "schedule", done: !!schedule.data, status: schedule.data ? t("plan.journey.scheduleDone", { count: schedule.data.lessons.length }) : t("plan.journey.scheduleTodo") },
    { key: "objectives", done: hasTree, busy, status: busy ? t("plan.generating") : b ? t("plan.versionLabel", { n: b.version, by: t(`plan.by.${b.generated_by}`), status: t(`plan.statuses.${b.status}`) }) : t("plan.journey.objectivesTodo") },
    { key: "review", done: hasTree && drafts === 0, status: !hasTree ? t("plan.journey.reviewTodo") : drafts > 0 ? t("plan.drafts", { count: drafts }) : t("plan.noDrafts") },
    {
      key: "lessons",
      done: !!allocation.data && allocation.data.lessons.length > 0 && allocation.data.unallocated.length === 0 && allocation.data.lessons.some((l) => l.lo_ids.length > 0) && (coverage.data?.open ?? 0) === 0,
      status: !allocation.data || allocation.data.lessons.length === 0 ? t("plan.journey.lessonsTodo") : allocation.data.unallocated.length > 0 ? t("plan.journey.lessonsLeft", { count: allocation.data.unallocated.length }) : (coverage.data?.open ?? 0) > 0 ? t("plan.journey.lessonsGaps", { count: coverage.data!.open }) : t("plan.journey.lessonsDone"),
    },
    { key: "items", done: (items.data?.length ?? 0) > 0, status: items.data ? t("course.itemCount", { count: items.data.length }) : "…" },
  ];
  const current: StepKey = step ?? (steps.find((x) => !x.done)?.key ?? "review");

  return (
    <TeacherChrome title={t("plan.title")} wide course={course.data ? { id: courseId, title: course.data.title } : null} stage="plan">
      <p className="measure -mt-3 text-[color:var(--ink-2)]">{t("plan.journey.lede")}</p>
      <Journey steps={steps} current={current} onPick={setStep} />

      {error && <p role="alert" className="rounded-[var(--radius-control)] border border-[color:var(--danger)] px-3 py-2 text-sm">{error}</p>}

      {current === "syllabus" && (
        <div className="rise" key="syllabus">
          <SyllabusPanel courseId={courseId} courseLang={course.data?.lang ?? null} syllabus={syllabus.data ?? null} onSaved={() => { void qc.invalidateQueries({ queryKey: ["syllabus", courseId] }); setStep("schedule"); }} />
        </div>
      )}
      {current === "schedule" && (
        <div className="rise" key="schedule">
          <SchedulePanel courseId={courseId} onLessonCount={() => setStep("objectives")} />
          <p className="mt-3 text-sm text-[color:var(--ink-2)]">
            {t("plan.journey.scheduleWhy")}{" "}
            <button type="button" className="underline" onClick={() => setStep("objectives")}>{t("plan.journey.skip")}</button>
          </p>
        </div>
      )}
      {current === "objectives" && (
        <div className="rise" key="objectives">
          <GeneratePanel
            courseId={courseId}
            hasSyllabus={!!syllabus.data}
            sessions={schedule.data?.lessons.length}
            breakdown={b ?? null}
            versions={versions.data ?? []}
            onGenerated={(nb) => { setVersionId(nb.breakdown_id); refresh(); }}
            onPickVersion={(id) => { setVersionId(id); setSelected(null); }}
            onReview={() => setStep("review")}
          />
        </div>
      )}
      {current === "review" && b && !busy && (
        <div className="rise flex flex-col gap-4" key="review">
          {/* A filter row above a dense tree: every control on the compact step (40 px), which is
              the one documented size below the default. */}
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-[color:var(--ink-2)]">{drafts > 0 ? t("plan.drafts", { count: drafts }) : t("plan.noDrafts")}</span>
            {drafts > 0 && <Button variant="secondary" size="sm" onClick={() => void onApproveAll()}>{t("plan.approveAll")}</Button>}
            <Button variant="secondary" size="sm" onClick={() => void run(async () => { await addModule(b.breakdown_id, { title: t("plan.module") }); })}>{t("plan.addModule")}</Button>
            {versions.data && versions.data.length > 1 && (
              <label className="ml-auto flex items-center gap-2">
                {t("plan.version")}
                <select className="field field-sm max-w-[22rem]" value={currentId ?? ""} onChange={(e) => { setVersionId(e.target.value); setSelected(null); }}>
                  {versions.data.map((v) => (
                    <option key={v.breakdown_id} value={v.breakdown_id}>
                      {t("plan.versionLabel", { n: v.version, by: t(`plan.by.${v.generated_by}`), status: t(`plan.statuses.${v.status}`) })}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <div className="grid gap-4 md:grid-cols-[minmax(15rem,1fr)_2fr] xl:grid-cols-[minmax(20rem,1fr)_2fr]">
            <section className={card}>
              <h2 className="font-semibold">{t("plan.tree")}</h2>
              {b.modules.length === 0 && <p className="text-sm text-[color:var(--ink-2)]">{t("plan.journey.emptyTree")}</p>}
              <BreakdownTree modules={b.modules} selected={selected} onSelect={setSelected} onApprove={(ref) => void approveNode(ref)} />
              <p className="text-xs text-[color:var(--ink-2)]">{t("plan.keys")}</p>
            </section>
            <section className={card}>
              {!node && <p className="text-sm text-[color:var(--ink-2)]">{t("plan.select")}</p>}
              {node?.kind === "module" && <ModulePanel key={node.module.module_id} m={node.module} b={b} run={run} onDeleted={() => setSelected(null)} onSelect={setSelected} />}
              {node?.kind === "topic" && <TopicPanel key={node.topic.topic_id} tp={node.topic} m={node.module} b={b} run={run} onDeleted={() => setSelected(null)} onSelect={setSelected} />}
              {node?.kind === "lo" && <LoPanel key={node.lo.lo_id} lo={node.lo} tp={node.topic} b={b} courseId={courseId} run={run} onDeleted={() => setSelected(null)} onSelect={setSelected} />}
            </section>
          </div>
          {b.notes && (
            <section className={card}>
              <h2 className="font-semibold">{t("plan.notes")}</h2>
              <p className="text-sm whitespace-pre-wrap">{b.notes}</p>
            </section>
          )}
          {drafts === 0 && b.modules.length > 0 && (
            <p className="text-sm">
              {t("plan.journey.reviewDone")}{" "}
              <button type="button" className="underline" onClick={() => setStep("lessons")}>{t("plan.journey.toLessons")}</button>
            </p>
          )}
        </div>
      )}
      {current === "review" && (!b || busy) && (
        <div className={card}>
          <p className="text-sm text-[color:var(--ink-2)]">{busy ? t("plan.generating") : t("plan.journey.reviewTodo")}</p>
          {!b && <Button variant="secondary" onClick={() => setStep("objectives")}>{t("plan.journey.objectives")}</Button>}
        </div>
      )}
      {current === "lessons" && (
        <div className="rise" key="lessons">
          <AllocationPanel
            courseId={courseId}
            allocation={allocation.data ?? null}
            los={course.data?.los ?? []}
            coverage={coverage.data ?? null}
            onChanged={() => {
              void qc.invalidateQueries({ queryKey: ["allocation", courseId] });
              void qc.invalidateQueries({ queryKey: ["coverage", courseId] });
            }}
            onNext={() => setStep("items")}
          />
        </div>
      )}
      {current === "items" && (
        <section className={`${card} rise`} key="items">
          <h2 className="display text-xl">{t("plan.journey.items")}</h2>
          <p className="text-sm text-[color:var(--ink-2)]">{t("plan.journey.itemsBody")}</p>
          <p className="text-sm">{items.data ? t("course.itemCount", { count: items.data.length }) : "…"}</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/t/c/$courseId/items" params={{ courseId }} className="inline-flex min-h-[var(--control-h)] items-center rounded-[var(--radius-control)] bg-[color:var(--accent)] px-5 font-semibold text-[color:var(--accent-ink)] hover:brightness-95">{t("plan.journey.openBank")}</Link>
            <Link to="/t/c/$courseId" params={{ courseId }} className="inline-flex min-h-[var(--control-h)] items-center rounded-[var(--radius-control)] border px-5 font-semibold hover:bg-[color:var(--surface-2)]">{t("plan.journey.toTeach")}</Link>
          </div>
        </section>
      )}
    </TeacherChrome>
  );
}

// --- the journey ------------------------------------------------------------------------------

type StepKey = "syllabus" | "schedule" | "objectives" | "review" | "lessons" | "items";
type StepInfo = { key: StepKey; done: boolean; busy?: boolean; status: string };

function Journey({ steps, current, onPick }: { steps: StepInfo[]; current: StepKey; onPick: (k: StepKey) => void }) {
  const { t } = useTranslation();
  return (
    <ol className="grid-fit" style={{ "--fit-min": "11rem", gap: "0.75rem" } as CSSProperties} aria-label={t("plan.journey.label")}>
      {steps.map((s, i) => {
        const active = s.key === current;
        return (
          <li key={s.key}>
            <button
              type="button"
              aria-current={active ? "step" : undefined}
              onClick={() => onPick(s.key)}
              className={[
                "flex h-full w-full flex-col gap-1 rounded-[var(--radius-card)] border p-4 text-left transition-[transform,box-shadow,border-color] duration-150 hover:-translate-y-0.5 hover:shadow-md",
                active ? "border-[color:var(--accent)] bg-[color:var(--surface)] shadow-sm" : "border-transparent bg-[color:var(--surface)] shadow-sm",
              ].join(" ")}
            >
              <span className="flex items-center justify-between">
                <span className="display text-xl">{i + 1}</span>
                <span aria-hidden className="text-sm" style={{ color: s.done ? "var(--ok)" : s.busy ? "var(--reconnecting)" : "var(--ink-2)" }}>
                  {s.done ? "✓" : s.busy ? "◐" : "○"}
                </span>
                <span className="sr-only">{s.done ? t("plan.journey.done") : s.busy ? t("plan.journey.busy") : t("plan.journey.todo")}</span>
              </span>
              <span className="font-semibold">{t(`plan.journey.${s.key}`)}</span>
              <span className="text-xs text-[color:var(--ink-2)]">{s.status}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

// --- syllabus -------------------------------------------------------------------------------

function SyllabusPanel({ courseId, courseLang, syllabus, onSaved }: { courseId: string; courseLang: string | null; syllabus: SyllabusText | null; onSaved: () => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const save = useMutation({ mutationFn: () => pasteSyllabus(courseId, text), onSuccess: () => { setText(""); setMsg(null); onSaved(); }, onError: (e) => setMsg(errText(e, t)) });
  const upload = useMutation({ mutationFn: (f: File) => uploadSyllabus(courseId, f), onSuccess: () => { setMsg(null); onSaved(); }, onError: (e) => setMsg(errText(e, t)) });
  return (
    <section className={card}>
      <h2 className="display text-xl">{t("plan.journey.syllabus")}</h2>
      <p className="text-sm text-[color:var(--ink-2)]">{syllabus ? t("plan.saved", { chars: syllabus.chars, source: syllabus.source }) : t("plan.journey.syllabusBody")}</p>
      {syllabus && (syllabus.ocr_status === "queued" || syllabus.ocr_status === "running") && (
        <p className="text-sm" role="status" aria-live="polite">{t("plan.ocr.running")}</p>
      )}
      {syllabus && syllabus.ocr_status === "failed" && <p className="text-sm" role="alert" style={{ color: "var(--danger)" }}>{t("plan.ocr.failed")}</p>}
      {syllabus && syllabus.ocr_status === "done" && <p className="text-sm text-[color:var(--ink-2)]">{t("plan.ocr.done")}</p>}
      {syllabus && syllabus.lang && courseLang && syllabus.lang !== courseLang && (
        <p className="text-sm" role="status">{t("plan.ocr.langMismatch", { detected: t(`plan.ocr.lang.${syllabus.lang}`), course: t(`plan.ocr.lang.${courseLang}`) })}</p>
      )}
      {syllabus && syllabus.excerpt && <p className="line-clamp-3 rounded-[var(--radius-control)] bg-[color:var(--surface-2)] p-3 text-sm">{syllabus.excerpt}</p>}
      <textarea className={input} rows={6} placeholder={t("plan.pasteHint")} value={text} onChange={(e) => setText(e.target.value)} aria-label={t("plan.syllabus")} />
      <div className="flex flex-wrap items-center gap-3">
        <Button disabled={text.trim().length < 40} loading={save.isPending} onClick={() => save.mutate()}>{syllabus ? t("plan.journey.replaceSyllabus") : t("plan.saveSyllabus")}</Button>
        <label className="inline-flex min-h-[var(--control-h)] cursor-pointer items-center rounded-[var(--radius-control)] border px-4 text-sm font-medium hover:bg-[color:var(--surface-2)]">
          {upload.isPending ? t("plan.journey.uploading") : t("plan.upload")}
          <input type="file" accept=".pdf,.docx,.txt" className="sr-only" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); }} />
        </label>
        {syllabus && <button type="button" className="text-sm underline" onClick={onSaved}>{t("plan.journey.next")}</button>}
      </div>
      {msg && <p role="alert" className="text-sm text-[color:var(--danger)]">{msg}</p>}
    </section>
  );
}

// --- objectives -------------------------------------------------------------------------------

function GeneratePanel({ courseId, hasSyllabus, sessions, breakdown, versions, onGenerated, onPickVersion, onReview }: { courseId: string; hasSyllabus: boolean; sessions?: number; breakdown: Breakdown | null; versions: Breakdown[]; onGenerated: (b: Breakdown) => void; onPickVersion: (id: string) => void; onReview: () => void }) {
  const { t, i18n } = useTranslation();
  const [msg, setMsg] = useState<string | null>(null);
  const busy = breakdown ? ["queued", "running"].includes(breakdown.status) : false;
  const host = useQuery({ queryKey: ["host-info"], queryFn: getHostInfo, retry: false });
  const noAi = noServerHere(host.data);
  const generate = useMutation({ mutationFn: () => generateBreakdown(courseId, sessions), onSuccess: onGenerated, onError: (e) => setMsg(errText(e, t)) });
  const manual = useMutation({ mutationFn: () => manualBreakdown(courseId), onSuccess: onGenerated, onError: (e) => setMsg(errText(e, t)) });
  return (
    <section className={card}>
      <h2 className="display text-xl">{t("plan.journey.objectives")}</h2>
      <p className="text-sm text-[color:var(--ink-2)]">{t("plan.journey.objectivesBody")}</p>
      {!hasSyllabus && <p className="text-sm">{t("plan.noSyllabus")}</p>}
      {busy && (
        <div className="flex items-center gap-3 rounded-[var(--radius-control)] bg-[color:var(--surface-2)] p-3 text-sm" aria-live="polite">
          <span aria-hidden className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          {t("plan.generating")}
        </div>
      )}
      {breakdown?.status === "failed" && <p role="alert" className="text-sm text-[color:var(--danger)]">{t("plan.failed", { error: breakdown.error ?? "" })}</p>}
      {breakdown && !busy && breakdown.status !== "failed" && (
        <p className="text-sm">
          {t("plan.journey.have", { modules: breakdown.counts.modules ?? breakdown.modules.length, los: breakdown.counts.los ?? 0 })}{" "}
          <button type="button" className="underline" onClick={onReview}>{t("plan.journey.review")}</button>
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button aria-describedby={noAi ? "no-ai" : undefined} disabled={!hasSyllabus || busy || noAi} loading={generate.isPending} onClick={() => generate.mutate()}>{breakdown ? t("plan.regenerate") : t("plan.generate")}</Button>
        {!breakdown && <Button variant="secondary" loading={manual.isPending} onClick={() => manual.mutate()}>{t("plan.createManual")}</Button>}
        {sessions ? <span className="text-xs text-[color:var(--ink-2)]">{t("plan.journey.sessionsHint", { count: sessions })}</span> : null}
      </div>
      {noAi && <p id="no-ai" className="text-sm text-[color:var(--ink-2)]">{t("plan.needsAi")}</p>}
      {msg && <p role="alert" className="text-sm text-[color:var(--danger)]">{msg}</p>}
      {versions.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-[color:var(--ink-2)]">{t("plan.history")}</summary>
          <ul className="mt-2 flex flex-col gap-1">
            {versions.map((v) => (
              <li key={v.breakdown_id} className="flex flex-wrap items-center gap-2">
                <button type="button" className="underline" onClick={() => onPickVersion(v.breakdown_id)}>
                  {t("plan.versionLabel", { n: v.version, by: t(`plan.by.${v.generated_by}`), status: t(`plan.statuses.${v.status}`) })}
                </button>
                <span className="text-xs text-[color:var(--ink-2)]">{new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(v.created_at))}</span>
                {v.model && <span className="text-xs text-[color:var(--ink-2)]">{v.model}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

// --- node panels ----------------------------------------------------------------------------

type Run = (fn: () => Promise<unknown>) => Promise<void>;

function Header({ kind, status, code }: { kind: string; status: string; code?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs uppercase tracking-wide text-[color:var(--ink-2)]">{t(`plan.${kind}`)}</span>
      {code && <span className="font-mono text-sm">{code}</span>}
      {status === "draft" ? <DraftChip /> : <span className="text-xs text-[color:var(--ok)]">✓ {t("plan.approved")}</span>}
    </div>
  );
}

function Actions({ status, onApprove, onDelete, onUp, onDown }: { status: string; onApprove: () => void; onDelete: () => void; onUp: () => void; onDown: () => void }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  return (
    <div className="flex flex-wrap gap-2">
      {status === "draft" && <Button onClick={onApprove}>{t("plan.approve")}</Button>}
      <Button variant="secondary" onClick={onUp}>↑ {t("plan.up")}</Button>
      <Button variant="secondary" onClick={onDown}>↓ {t("plan.down")}</Button>
      <Button
        variant="destructive"
        onClick={async () => {
          if (await confirm({ title: t("plan.deleteTitle"), body: t("plan.deleteConfirm"), confirmLabel: t("plan.delete"), destructive: true })) onDelete();
        }}
      >
        {t("plan.delete")}
      </Button>
    </div>
  );
}

function ModulePanel({ m, b, run, onDeleted, onSelect }: { m: ModuleNode; b: Breakdown; run: Run; onDeleted: () => void; onSelect: (r: NodeRef) => void }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(m.title);
  const [milestone, setMilestone] = useState(m.milestone);
  const [newTopic, setNewTopic] = useState("");
  const dirty = title !== m.title || milestone !== m.milestone;
  return (
    <>
      <Header kind="module" status={m.review_status} />
      <label className="flex flex-col gap-1 text-sm">{t("plan.titleField")}<input className={input} value={title} onChange={(e) => setTitle(e.target.value)} /></label>
      <label className="flex flex-col gap-1 text-sm">{t("plan.milestone")}<textarea className={input} rows={2} value={milestone} onChange={(e) => setMilestone(e.target.value)} /></label>
      {dirty && <Button onClick={() => void run(() => patchModule(m.module_id, { title, milestone }))}>{t("plan.save")}</Button>}
      <Actions
        status={m.review_status}
        onApprove={() => void run(() => patchModule(m.module_id, { review_status: "approved" }))}
        onDelete={() => void run(async () => { await deleteModule(m.module_id); onDeleted(); })}
        onUp={() => void run(() => patchModule(m.module_id, { position: Math.max(0, m.position - 1) }))}
        onDown={() => void run(() => patchModule(m.module_id, { position: Math.min(b.modules.length - 1, m.position + 1) }))}
      />
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); void run(async () => { const tp = await addTopic(m.module_id, { title: newTopic }); setNewTopic(""); onSelect({ kind: "topic", id: tp.topic_id }); }); }}>
        <input className={input} placeholder={t("plan.topic")} value={newTopic} onChange={(e) => setNewTopic(e.target.value)} />
        <Button variant="secondary" type="submit" disabled={!newTopic.trim()}>{t("plan.addTopic")}</Button>
      </form>
    </>
  );
}

function TopicPanel({ tp, m, b, run, onDeleted, onSelect }: { tp: TopicNode; m: ModuleNode; b: Breakdown; run: Run; onDeleted: () => void; onSelect: (r: NodeRef) => void }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(tp.title);
  const [lo, setLo] = useState({ code: "", text: "", bloom: "" });
  return (
    <>
      <Header kind="topic" status={tp.review_status} />
      <label className="flex flex-col gap-1 text-sm">{t("plan.titleField")}<input className={input} value={title} onChange={(e) => setTitle(e.target.value)} /></label>
      {title !== tp.title && <Button onClick={() => void run(() => patchTopic(tp.topic_id, { title }))}>{t("plan.save")}</Button>}
      <label className="flex items-center gap-2 text-sm">
        {t("plan.moveTo")}
        <select className="field" value={m.module_id} onChange={(e) => void run(() => patchTopic(tp.topic_id, { module_id: e.target.value }))}>
          {b.modules.map((mm) => <option key={mm.module_id} value={mm.module_id}>{mm.title}</option>)}
        </select>
      </label>
      <Actions
        status={tp.review_status}
        onApprove={() => void run(() => patchTopic(tp.topic_id, { review_status: "approved" }))}
        onDelete={() => void run(async () => { await deleteTopic(tp.topic_id); onDeleted(); })}
        onUp={() => void run(() => patchTopic(tp.topic_id, { position: Math.max(0, tp.position - 1) }))}
        onDown={() => void run(() => patchTopic(tp.topic_id, { position: Math.min(m.topics.length - 1, tp.position + 1) }))}
      />
      <form className="flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); void run(async () => { const n = await addTreeLo(tp.topic_id, { code: lo.code, text: lo.text, bloom: lo.bloom || null }); setLo({ code: "", text: "", bloom: "" }); onSelect({ kind: "lo", id: n.lo_id }); }); }}>
        <div className="flex gap-2">
          <input className={input} style={{ maxWidth: "8rem" }} placeholder={t("plan.code")} value={lo.code} onChange={(e) => setLo({ ...lo, code: e.target.value })} />
          <select className="field" value={lo.bloom} onChange={(e) => setLo({ ...lo, bloom: e.target.value })}>
            <option value="">{t("editor.bloom")}</option>
            {BLOOMS.map((bl) => <option key={bl} value={bl}>{t(`editor.blooms.${bl}`)}</option>)}
          </select>
        </div>
        <input className={input} placeholder={t("plan.text")} value={lo.text} onChange={(e) => setLo({ ...lo, text: e.target.value })} />
        <Button variant="secondary" type="submit" disabled={!lo.code.trim() || lo.text.trim().length < 3}>{t("plan.addObjective")}</Button>
      </form>
    </>
  );
}

function LoPanel({ lo, tp, b, courseId, run, onDeleted, onSelect }: { lo: LoNode; tp: TopicNode; b: Breakdown; courseId: string; run: Run; onDeleted: () => void; onSelect: (r: NodeRef) => void }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [text, setText] = useState(lo.text);
  const [code, setCode] = useState(lo.code);
  const [bloom, setBloom] = useState(lo.bloom ?? "");
  const [split, setSplit] = useState<{ a: string; b: string } | null>(null);
  const [mergeId, setMergeId] = useState("");
  const all = b.modules.flatMap((m) => m.topics.flatMap((x) => x.los));
  const byId = new Map(all.map((x) => [x.lo_id, x]));
  const others = all.filter((x) => x.lo_id !== lo.lo_id);
  const dirty = text !== lo.text || code !== lo.code || bloom !== (lo.bloom ?? "");
  const topics = b.modules.flatMap((m) => m.topics.map((x) => ({ ...x, moduleTitle: m.title })));
  return (
    <>
      <Header kind="objective" status={lo.review_status} code={lo.code} />
      <label className="flex flex-col gap-1 text-sm">{t("plan.text")}<textarea className={input} rows={3} value={text} onChange={(e) => setText(e.target.value)} /></label>
      <div className="flex flex-wrap gap-3">
        <label className="flex flex-col gap-1 text-sm">{t("plan.code")}<input className={input} style={{ maxWidth: "8rem" }} value={code} onChange={(e) => setCode(e.target.value)} /></label>
        <label className="flex flex-col gap-1 text-sm">
          {t("editor.bloom")}
          <select className="field" value={bloom} onChange={(e) => setBloom(e.target.value)}>
            <option value="">—</option>
            {BLOOMS.map((bl) => <option key={bl} value={bl}>{t(`editor.blooms.${bl}`)}</option>)}
          </select>
        </label>
      </div>
      {dirty && <Button onClick={() => void run(() => patchLo(lo.lo_id, { text, code, bloom: bloom || null }))}>{t("plan.save")}</Button>}

      <div className="flex flex-col gap-1 text-sm">
        <span>{t("plan.prerequisites")}</span>
        <div className="flex flex-wrap items-center gap-2">
          {lo.prerequisites.length === 0 && <span className="text-[color:var(--ink-2)]">{t("plan.none")}</span>}
          {lo.prerequisites.map((pid) => (
            <span key={pid} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs">
              <button type="button" className="underline" onClick={() => onSelect({ kind: "lo", id: pid })}>{byId.get(pid)?.code ?? pid}</button>
              <button type="button" aria-label={t("plan.delete")} onClick={() => void run(() => patchLo(lo.lo_id, { prerequisites: lo.prerequisites.filter((x) => x !== pid) }))}>×</button>
            </span>
          ))}
          <select className="field field-sm max-w-[18rem]" aria-label={t("plan.addPrerequisite")} value="" onChange={(e) => { if (e.target.value) void run(() => patchLo(lo.lo_id, { prerequisites: [...lo.prerequisites, e.target.value] })); }}>
            <option value="">{t("plan.addPrerequisite")}</option>
            {others.filter((x) => !lo.prerequisites.includes(x.lo_id)).map((x) => <option key={x.lo_id} value={x.lo_id}>{x.code} · {x.text.length > 60 ? `${x.text.slice(0, 60)}…` : x.text}</option>)}
          </select>
        </div>
      </div>

      <p className="text-sm">
        {t("plan.items", { n: lo.item_count })}{" "}
        <Link to="/t/c/$courseId/items" params={{ courseId }} className="underline">{t("plan.reviewItems")}</Link>
      </p>

      <label className="flex items-center gap-2 text-sm">
        {t("plan.moveTo")}
        <select className="field max-w-[24rem]" value={tp.topic_id} onChange={(e) => void run(() => patchLo(lo.lo_id, { topic_id: e.target.value }))}>
          {topics.map((x) => <option key={x.topic_id} value={x.topic_id}>{x.moduleTitle} › {x.title}</option>)}
        </select>
      </label>

      <Actions
        status={lo.review_status}
        onApprove={() => void run(() => patchLo(lo.lo_id, { review_status: "approved" }))}
        onDelete={() => void run(async () => { await deleteLo(lo.lo_id); onDeleted(); })}
        onUp={() => void run(() => patchLo(lo.lo_id, { position: Math.max(0, lo.position - 1) }))}
        onDown={() => void run(() => patchLo(lo.lo_id, { position: Math.min(tp.los.length - 1, lo.position + 1) }))}
      />

      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => setSplit(split ? null : { a: lo.text, b: "" })}>{t("plan.split")}</Button>
        <select className="field max-w-[20rem]" aria-label={t("plan.mergeInto")} value={mergeId} onChange={(e) => setMergeId(e.target.value)}>
          <option value="">{t("plan.merge")}…</option>
          {others.map((x) => <option key={x.lo_id} value={x.lo_id}>{x.code} · {x.text.length > 50 ? `${x.text.slice(0, 50)}…` : x.text}</option>)}
        </select>
        {mergeId && (
          <Button
            variant="secondary"
            onClick={async () => {
              const o = byId.get(mergeId);
              if (!o) return;
              if (await confirm({ title: t("plan.mergeTitle", { code: o.code }), body: t("plan.mergeConfirm"), confirmLabel: t("plan.merge"), destructive: true })) {
                await run(async () => { await mergeLo(lo.lo_id, mergeId); setMergeId(""); });
              }
            }}
          >
            {t("plan.merge")}
          </Button>
        )}
      </div>
      {split && (
        <form className="flex flex-col gap-2 rounded-[var(--radius-card)] border p-3" onSubmit={(e) => { e.preventDefault(); void run(async () => { await splitLo(lo.lo_id, { text_a: split.a, text_b: split.b }); setSplit(null); }); }}>
          <label className="flex flex-col gap-1 text-sm">{t("plan.splitA")}<textarea className={input} rows={2} value={split.a} onChange={(e) => setSplit({ ...split, a: e.target.value })} /></label>
          <label className="flex flex-col gap-1 text-sm">{t("plan.splitB")}<textarea className={input} rows={2} value={split.b} onChange={(e) => setSplit({ ...split, b: e.target.value })} /></label>
          <Button type="submit" disabled={split.a.trim().length < 3 || split.b.trim().length < 3}>{t("plan.doSplit")}</Button>
        </form>
      )}
    </>
  );
}
